import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  LocalStorageKeys,
  PreferenceKey,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useConfiguration, useUpdatePreferences } from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  AttributeSetting,
  PreferenceFilter,
  PreferenceOption,
  SearchTerm,
  SearchTermType,
} from "@is/types";
import {
  getPrimaryAttributesWithSelectedSearchTerms,
  getTranslatedValue,
} from "@is/utils/attributes";

type OnNavigateFunction = (searchTerms: SearchTerm[]) => void;

const useManagePrimaryAttributeDrawer = (onNavigate: OnNavigateFunction) => {
  const { t } = useTranslation(TranslationContext.ProductListing);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [initialSearchTermsAreSet, setInitialSearchTermsAreSet] =
    useState(false);

  const [searchTerms, setSearchTerms] = useState<SearchTerm[]>([]);

  const {
    showPrimaryAttributeDrawer,
    updateViewState,
    showLanguageAndCurrencyModal,
    showPrimaryAttributeButtons,
    showSignUpModal,
    signUpStatusModal,
  } = useContext(ViewStateContext).content;

  const [{ data: preferences, isPending }, updatePreferences] =
    useUpdatePreferences();

  const countryCode = LocalStorageService.getItem(LocalStorageKeys.CountryCode);

  const { loyaltySignupForm, attributes } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const primaryAttributes = useMemo(
    () => attributes.filter((attr) => attr.primary),
    [attributes]
  );

  const primaryAttributesWithOptions = useMemo(() => {
    const attributesWitOptions = getPrimaryAttributesWithSelectedSearchTerms(
      primaryAttributes,
      searchTerms
    );

    return attributesWitOptions
      ? attributesWitOptions?.filter((attribute) => attribute.visible)
      : [];
  }, [primaryAttributes, searchTerms]);

  const filters: PreferenceFilter[] = useMemo(
    () =>
      attributes
        ?.filter((attribute) => !attribute.custom)
        .map(({ key }) => ({
          key,
          sortBy: "Label",
        })),
    [attributes]
  );

  const currentOptions = useMemo(() => {
    if (!primaryAttributesWithOptions || !preferences || currentStep === null)
      return [];

    const currentAttribute = primaryAttributesWithOptions[currentStep];
    const currentPreference = preferences.find(
      (preference) => preference?.key === currentAttribute?.key
    );

    if (currentPreference) {
      return currentPreference.options?.map((option) => ({
        key: option.parentKey,
        value: option.key,
        type: option.type,
        label:
          option.type === SearchTermType.Size ||
          option.type === SearchTermType.Length
            ? `${option.type}: ${option.label}`
            : option.label,
        isPrimary: true,
      }));
    }

    return [];
  }, [primaryAttributesWithOptions, preferences, currentStep]);

  const showSignUpButton = useMemo(() => {
    const countries = loyaltySignupForm?.countries ?? [];

    const isAllowedInSelectedCountry =
      countries.length === 0 || countries.includes(countryCode);

    return loyaltySignupForm?.isEnabled && isAllowedInSelectedCountry;
  }, [countryCode, loyaltySignupForm]);

  const signUpButtonTitle = useMemo(
    () =>
      getTranslatedValue(loyaltySignupForm?.title?.translations || []) ??
      loyaltySignupForm?.title?.value ??
      "",
    [loyaltySignupForm]
  );

  const handleToggleLanguageAndCurrencyModal = useCallback(() => {
    updateViewState({
      showLanguageAndCurrencyModal: !showLanguageAndCurrencyModal,
    });

    trackEvent({
      eventType: showLanguageAndCurrencyModal
        ? TrackingEventTypes.LanguageAndCurrencyModalClose
        : TrackingEventTypes.LanguageAndCurrencyModalOpen,
      data: {},
    });
  }, [showLanguageAndCurrencyModal, updateViewState]);

  const handleToggleSignUpModal = useCallback(() => {
    if (!showSignUpModal) {
      updateViewState({
        showSignUpModal: true,
      });
    }
  }, [showSignUpModal, updateViewState]);

  const handleToggleDrawer = useCallback(() => {
    updateViewState({ showPrimaryAttributeButtons: false });

    if (showPrimaryAttributeDrawer || currentStep !== null) {
      updateViewState({
        showPrimaryAttributeDrawer: !showPrimaryAttributeDrawer,
      });

      trackEvent({
        eventType: TrackingEventTypes.PrimaryAttributesDrawerOpen,
        data: {},
      });

      return;
    }

    onNavigate(searchTerms);
  }, [
    showPrimaryAttributeDrawer,
    updateViewState,
    currentStep,
    onNavigate,
    searchTerms,
  ]);

  const handleCloseDrawer = useCallback(() => {
    updateViewState({
      showPrimaryAttributeDrawer: false,
      showPrimaryAttributeButtons: true,
    });
  }, [updateViewState]);

  const getPreferences = useCallback(
    async (
      filters: PreferenceFilter[],
      primaryAttributes: AttributeSetting[],
      searchTerms: SearchTerm[]
    ) => {
      const appliedPreferenceOptions =
        getPrimaryAttributesWithSelectedSearchTerms(
          primaryAttributes,
          searchTerms
        );

      const preparedOptions = appliedPreferenceOptions.reduce(
        (acc: PreferenceOption[], attribute) => {
          if (!attribute.options.length) return acc;

          acc.push({
            key: attribute.key,
            ...(attribute.options.length
              ? {
                  options: attribute.options.map(({ key }) => ({
                    key,
                  })),
                }
              : {}),
          });
          return acc;
        },
        []
      );

      const data = await updatePreferences({
        filters: filters,
        appliedPreferenceOptions: searchTerms.length ? preparedOptions : null,
      });

      return data?.map((preference: PreferenceOption, index: number) => ({
        ...preference,
        options: preference.options.map((item: PreferenceOption) => ({
          ...item,
          parentIndex:
            preference.key === PreferenceKey.Category ? index : undefined,
          parentKey: preference.key,
          options: item.options
            ? item.options.map((subItem: PreferenceOption) => ({
                ...subItem,
                parentKey: PreferenceKey.Subcategory,
              }))
            : [],
        })),
      }));
    },
    [updatePreferences]
  );

  const goToNextStep = useCallback(
    async (searchTerms: SearchTerm[]) => {
      let step = currentStep ?? -1;

      const updatedPreferences = await getPreferences(
        filters,
        primaryAttributes,
        searchTerms
      );

      const newPrimaryAttributes = getPrimaryAttributesWithSelectedSearchTerms(
        primaryAttributes,
        searchTerms
      );

      const visibleNewPrimaryAttributes = newPrimaryAttributes.filter(
        (attribute) => attribute.visible
      );

      visibleNewPrimaryAttributes?.every(
        ({ key, type, options, visible }, index) => {
          if (index <= step || options.length > 0 || !visible) return true;

          const availableOptions =
            updatedPreferences?.find(
              (preference) => preference.key === key && preference.type === type
            )?.options ?? [];

          if (availableOptions.length === 0) return true;

          step = index;
          setCurrentStep(index);
        }
      );

      if (step !== currentStep) {
        return;
      }
      setCurrentStep(null);
      updateViewState({ showPrimaryAttributeDrawer: false });

      onNavigate(searchTerms);
    },
    [
      currentStep,
      getPreferences,
      filters,
      primaryAttributes,
      updateViewState,
      onNavigate,
    ]
  );

  const handlePrimaryAttributeClick = useCallback(
    (event: React.SyntheticEvent<HTMLButtonElement>) => {
      const attributeIndex = event.currentTarget.getAttribute(
        "data-attribute-index"
      );

      if (attributeIndex === null) return;

      if (
        primaryAttributesWithOptions[parseInt(attributeIndex)]?.options.length >
        0
      ) {
        onNavigate(searchTerms);
      } else {
        setCurrentStep(parseInt(attributeIndex));
      }
    },
    [onNavigate, primaryAttributesWithOptions, searchTerms]
  );

  const handleSelectOption = useCallback(
    (searchTerm: SearchTerm) => {
      const newSearchTerms = [...searchTerms, searchTerm];

      setSearchTerms(newSearchTerms);

      trackEvent({
        eventType: TrackingEventTypes.SearchTermAdded,
        data: { terms: [searchTerm] },
      });

      goToNextStep(newSearchTerms);
    },
    [searchTerms, setSearchTerms, goToNextStep]
  );

  const setInitialSearchTermsAndFirstStep = useCallback(async () => {
    if (
      initialSearchTermsAreSet ||
      !primaryAttributes ||
      filters.length === 0
    ) {
      return;
    }

    const initialPreferences = await getPreferences(
      filters,
      primaryAttributes,
      []
    );

    if (!initialPreferences) return;

    const searchTerms = primaryAttributes.reduce(
      (acc: SearchTerm[], attribute: AttributeSetting) => [
        ...acc,
        ...(attribute.term ? [{ ...attribute.term, isPrimary: true }] : []),
      ],
      []
    );

    const filteredSearchTerms = initialPreferences.reduce(
      (acc: SearchTerm[], current: PreferenceOption) => {
        searchTerms.forEach((searchTerm: SearchTerm) => {
          if (
            current.options.some(
              (preferenceOption: PreferenceOption) =>
                preferenceOption.key === searchTerm.value &&
                preferenceOption.type === searchTerm.type &&
                preferenceOption.parentKey === searchTerm.key
            )
          ) {
            acc.push({ ...searchTerm, isPrimary: true });
          }
        });
        return acc;
      },
      []
    );

    setSearchTerms(filteredSearchTerms);
    setInitialSearchTermsAreSet(true);

    const updatedPreferences = await getPreferences(
      filters,
      primaryAttributes,
      filteredSearchTerms
    );

    const visiblePrimaryAttributes = primaryAttributes.filter(
      (attribute) => attribute.visible
    );

    visiblePrimaryAttributes.every((attribute, index) => {
      if (
        attribute.term &&
        filteredSearchTerms.some((term) => term.value === attribute.term.value)
      )
        return true;

      const availableOptions =
        updatedPreferences?.find(
          (preference) =>
            preference.key === attribute.key &&
            preference.type === attribute.type
        )?.options ?? [];

      if (availableOptions.length === 0) return true;

      setCurrentStep(index);
    });
  }, [filters, primaryAttributes, initialSearchTermsAreSet, getPreferences]);

  const handleSettingDrawerOpen = useCallback(() => {
    updateViewState({ showSettingsDrawer: true });
  }, [updateViewState]);

  useEffect(() => {
    setInitialSearchTermsAndFirstStep();
  }, [setInitialSearchTermsAndFirstStep]);

  return {
    t,
    isOpen:
      showPrimaryAttributeDrawer &&
      !showLanguageAndCurrencyModal &&
      !showSignUpModal &&
      !signUpStatusModal.isOpen,
    initialSearchTermsAreSet,
    isPending,
    showPrimaryAttributeButtons,
    handleToggleDrawer,
    handleCloseDrawer,
    attributes: primaryAttributesWithOptions,
    currentOptions,
    handlePrimaryAttributeClick,
    handleSelectOption,
    showLanguageAndCurrencyModal,
    handleToggleLanguageAndCurrencyModal,
    handleSettingDrawerOpen,
    handleToggleSignUpModal,
    showSignUpModal,
    showSignUpButton,
    signUpButtonTitle,
  };
};

export default useManagePrimaryAttributeDrawer;
